import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchMetadataFields, fetchMetadataDistinct, forceRefreshMetadata } from '../store/slices/metadataSlice';

// Optimized hook that doesn't trigger useEffect on every render
export const useMetadataFields = () => {
  const dispatch = useAppDispatch();
  const { data, loading, error, lastFetched } = useAppSelector((state) => state.metadata.fields);

  const refetch = useCallback(() => {
    dispatch(fetchMetadataFields());
  }, [dispatch]);

  const forceRefresh = useCallback(() => {
    dispatch(fetchMetadataFields({})); // Force fetch by clearing cache
  }, [dispatch]);

  return useMemo(() => ({
    data,
    loading,
    error,
    lastFetched,
    refetch,
    forceRefresh,
  }), [data, loading, error, lastFetched, refetch, forceRefresh]);
};

// Optimized hook for metadata distinct values
export const useMetadataDistinct = () => {
  const dispatch = useAppDispatch();
  const { data, loading, error, lastFetched } = useAppSelector((state) => state.metadata.distinct);

  const refetch = useCallback(() => {
    dispatch(fetchMetadataDistinct());
  }, [dispatch]);

  const forceRefresh = useCallback(() => {
    dispatch(fetchMetadataDistinct({})); // Force fetch by clearing cache
  }, [dispatch]);

  return useMemo(() => ({
    data,
    loading,
    error,
    lastFetched,
    refetch,
    forceRefresh,
  }), [data, loading, error, lastFetched, refetch, forceRefresh]);
};

// Combined hook for both metadata types - now just reads from Redux state
export const useMetadata = () => {
  const fields = useMetadataFields();
  const distinct = useMetadataDistinct();
  const dispatch = useAppDispatch();

  const refreshAll = useCallback(() => {
    dispatch(forceRefreshMetadata());
  }, [dispatch]);

  return useMemo(() => ({
    fields,
    distinct,
    // Combined loading state - only show loading if we don't have any data
    loading: (fields.loading && !fields.data) || (distinct.loading && !distinct.data),
    // Combined error state
    error: fields.error || distinct.error,
    refreshAll,
  }), [fields, distinct, refreshAll]);
};
